import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Flex, Modal, Skeleton, Tag, Typography, message } from '@/shared/antd-imports';
import { DownloadOutlined, LinkOutlined } from '@ant-design/icons';
import { useGetDocumentDownloadMutation, useGetProjectDocumentQuery } from '@/store/api';
import type { ClientDocumentVersion, ClientProjectDocument } from '@/types/project-documents';
import { formatCalendarDate } from '@/utils/money';
import { approvalTagColor, openDownload } from './document-utils';

interface DocumentVersionsModalProps {
  projectId: string;
  document: ClientProjectDocument;
  onClose: () => void;
}

/**
 * Owner decision 2026-10-15 (dec2): the versions of a shared document this client may open. The server lists only the
 * latest version, the versions the client approved or rejected, and earlier versions when the provider allows them.
 * Loading, error (retry) and empty states.
 */
export const DocumentVersionsModal: React.FC<DocumentVersionsModalProps> = ({ projectId, document, onClose }) => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, isFetching, refetch } = useGetProjectDocumentQuery({ projectId, documentId: document.id });
  const [download] = useGetDocumentDownloadMutation();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const versions = data?.body?.versions ?? [];

  const openVersion = async (version: ClientDocumentVersion) => {
    setDownloadingId(version.id);
    try {
      const result = await download({ projectId, documentId: document.id, versionId: version.id }).unwrap();
      if (!(result.done && result.body && openDownload(result.body))) {
        message.error(t('documents.downloadError', { defaultValue: 'The file could not be opened. Please try again.' }));
      }
    } catch {
      message.error(t('documents.downloadError', { defaultValue: 'The file could not be opened. Please try again.' }));
    } finally {
      setDownloadingId(null);
    }
  };

  let body: React.ReactNode;
  if (isLoading) {
    body = <Skeleton active paragraph={{ rows: 2 }} title={false} />;
  } else if (isError) {
    body = (
      <Alert
        type="error"
        showIcon
        message={t('documents.versionsError', { defaultValue: 'The versions could not be loaded.' })}
        action={
          <Button size="small" onClick={() => refetch()} loading={isFetching}>
            {t('documents.retry', { defaultValue: 'Try again' })}
          </Button>
        }
      />
    );
  } else if (versions.length === 0) {
    body = <Typography.Text type="secondary">{t('documents.versionsEmpty', { defaultValue: 'No versions to show.' })}</Typography.Text>;
  } else {
    body = (
      <Flex vertical gap={8} role="list" aria-label={t('documents.versionsTitle', { defaultValue: 'Versions of {{title}}', title: document.title })}>
        {versions.map(version => (
          <Flex key={version.id} role="listitem" justify="space-between" align="center" gap={8} wrap="wrap">
            <Flex vertical gap={2} style={{ minWidth: 0 }}>
              <Flex gap={6} align="center" wrap="wrap">
                <Typography.Text strong>{t('documents.version', { defaultValue: 'Version {{no}}', no: version.version_no })}</Typography.Text>
                {version.is_current && <Tag color="blue">{t('documents.latest', { defaultValue: 'Latest' })}</Tag>}
                {(version.approval_status === 'approved' || version.approval_status === 'rejected') && (
                  <Tag color={approvalTagColor(version.approval_status)}>
                    {version.approval_status === 'approved'
                      ? t('documents.approved', { defaultValue: 'Approved' })
                      : t('documents.rejected', { defaultValue: 'Rejected' })}
                  </Tag>
                )}
              </Flex>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {[
                  version.is_link ? t('documents.link', { defaultValue: 'Link' }) : version.file_name,
                  formatCalendarDate(version.uploaded_at.slice(0, 10), i18n.language),
                ].filter(Boolean).join(' · ')}
              </Typography.Text>
            </Flex>
            <Button
              size="small"
              icon={version.is_link ? <LinkOutlined /> : <DownloadOutlined />}
              loading={downloadingId === version.id}
              disabled={!version.available}
              onClick={() => void openVersion(version)}
              aria-label={t('documents.downloadVersion', { defaultValue: 'Download version {{no}}', no: version.version_no })}
            >
              {version.is_link ? t('documents.open', { defaultValue: 'Open' }) : t('documents.download', { defaultValue: 'Download' })}
            </Button>
          </Flex>
        ))}
      </Flex>
    );
  }

  return (
    <Modal
      open
      title={t('documents.versionsTitle', { defaultValue: 'Versions of {{title}}', title: document.title })}
      onCancel={onClose}
      footer={<Button onClick={onClose}>{t('documents.close', { defaultValue: 'Close' })}</Button>}
    >
      {body}
    </Modal>
  );
};

export default DocumentVersionsModal;
